// src/pages/Catalog/ProductOverviewPage.jsx
//
// Product Overview Page
// =============================================================================
//
// PURPOSE
// -------
// Backoffice overview screen for product catalog records.
//
// This page intentionally mirrors the current overview-page architecture used by
// ManagePage and VoucherOverviewPage:
// - compact toolbar,
// - compact filter drawer,
// - scanner-friendly barcode lookup,
// - sticky table header,
// - client-side pagination through the shared hook,
// - pagination footer as a normal flex child inside the table area,
// - edit / activate / delete actions.
//
// IMPORTANT ARCHITECTURE
// ----------------------
// Product maintenance is intentionally split into two screens:
//
//   ProductOverviewPage
//     - browsing,
//     - filtering,
//     - quick barcode lookup,
//     - activate/deactivate/delete actions.
//
//   ProductPage
//     - create product,
//     - edit product,
//     - image upload,
//     - full product details.
//
// This follows the same architectural idea as:
// - VoucherOverviewPage / VoucherPage,
// - ManagePage / detail-maintenance pages.
//
// PRODUCT MODEL ASSUMPTION
// ------------------------
// A product row represents one sellable / stock-manageable article variant,
// also called SKU.
//
// Example:
//   Product group: Finezja Deodorant Blue
//   Product row 1: FIN-DEO-BLUE-100ML / 100 ml
//   Product row 2: FIN-DEO-BLUE-200ML / 200 ml
//
// This overview therefore displays product variant fields:
// - sku,
// - barcode,
// - variant_group_key,
// - variant_label,
// - packaging_label,
// - content_quantity + content_unit_code,
// - stock_unit_code,
// - price_gross,
// - VAT,
// - active / publish flags.
//
// BARCODE MODE
// ------------
// Barcode mode performs an exact product lookup by barcode using:
//
//   GET /catalog/products/by-barcode/:barcode
//
// This is intentionally separate from normal list filtering.
// It prepares the future warehouse / goods receipt flow:
//
//   scan barcode
//   -> find product
//   -> receive stock
//
// PAGINATION ARCHITECTURE
// -----------------------
// ProductOverview now uses the same stable pagination direction as ManagePage
// and the current VoucherOverviewPage:
//
//   src/hooks/useClientPagination.js
//   src/components/common/PaginationFooter.jsx
//   src/components/common/PaginationFooter.css
//
// Important decisions:
// - useClientPagination.js stays unchanged.
// - The official returned field is `paginatedItems`.
// - Do NOT destructure alternative names such as `items`, `pageItems`,
//   `currentItems`, or `pagedItems`.
// - Pagination is NOT fixed to the viewport.
// - Pagination is a normal flex child inside `.po-table-wrap`.
// - `.po-table-scroller` takes the remaining height above the footer.
// - There is no useFixedFooterClearance, no footer measurement, no fixed footer,
//   and no artificial scroll spacer.
//
// CSS
// ---
// This overview uses its own dedicated CSS file:
//
//   ProductOverviewPage.css
//
// It intentionally does NOT use ProductCatalog.css, because the overview will
// become more complex and should not be coupled to the product edit form.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  fetchProducts,
  fetchProductByBarcode,
  fetchProductImageBlob,
  toggleProductActive,
  deleteProduct,
} from "../../services/productsService";
import { fetchProductCategories } from "../../services/productCategoriesService";
import { isRequestCanceled } from "../../services/httpUtils";
import useClientPagination from "../../hooks/useClientPagination";
import PaginationFooter from "../../components/common/PaginationFooter";
import { useTranslation } from "react-i18next";

import "./ProductOverviewPage.css";

/* -------------------------------------------------------------------------- */
/* Small local utilities                                                       */
/* -------------------------------------------------------------------------- */

function asBool(v) {
  if (v === true || v === false) return v;
  const s = String(v ?? "").trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

function isNonEmpty(v) {
  return String(v ?? "").trim() !== "";
}

function formatDecimal(v, digits = 2) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : String(v);
}

function formatMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : String(v);
}

/**
 * Build a stable return URL for leaving the overview and coming back later.
 *
 * This is the same UX pattern already introduced in VoucherOverview:
 * - the overview owns its current filter/pagination state,
 * - that state is reflected in the URL,
 * - edit/create pages receive the full return path through React Router state.
 *
 * Example:
 *   /catalog/products?q=deodorant&activity=Active&publish=all&page=2&pageSize=50
 *
 * ProductPage should not know how ProductOverview filters work.
 * It only receives a ready-to-use returnTo path.
 */
function buildReturnToPath(location) {
  const pathname = location?.pathname || "/catalog/products";
  const search = location?.search || "";

  return `${pathname}${search}`;
}

/**
 * Read a comma-separated URL list.
 *
 * Used for multi-value filters such as:
 *   categories=1,2,5
 *   activity=Active,Inactive
 */
function readCsvParam(searchParams, key) {
  const raw = String(searchParams.get(key) || "").trim();

  if (!raw) return [];

  return raw
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * Read a positive integer from URL query params.
 *
 * Invalid or missing values fall back to the provided default.
 */
function readPositiveIntParam(searchParams, key, fallback) {
  const n = Number(searchParams.get(key));

  if (!Number.isFinite(n) || n <= 0) return fallback;

  return Math.trunc(n);
}

/**
 * Compare arrays shallowly after string normalization.
 *
 * This prevents unnecessary state updates when URL values and current state are
 * already equivalent.
 */
function sameStringArray(a, b) {
  const left = Array.isArray(a) ? a.map(String) : [];
  const right = Array.isArray(b) ? b.map(String) : [];

  if (left.length !== right.length) return false;

  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return false;
  }

  return true;
}

function getProductCategoryName(row) {
  return row?.category?.name_public || row?.category?.name_internal || "—";
}

function getUnitSymbol(unitObj, fallbackCode) {
  return unitObj?.symbol || fallbackCode || "";
}

function formatContent(row) {
  if (row?.content_quantity == null || row?.content_quantity === "") return "—";

  const qty = formatDecimal(row.content_quantity, 3).replace(/\.?0+$/, "");
  const unit = getUnitSymbol(row.contentUnit, row.content_unit_code);

  return `${qty}${unit ? ` ${unit}` : ""}`;
}

function getPrimaryImage(row) {
  const images = Array.isArray(row?.images) ? row.images : [];
  if (!images.length) return null;
  return images.find((img) => asBool(img.is_primary)) || images[0];
}

function imageList(row) {
  return Array.isArray(row?.images) ? row.images : [];
}

/**
 * Product image thumbnail.
 *
 * Images are loaded through the product service as blobs so that protected/local
 * file handling remains centralized in the service layer.
 */
function ProductImageThumb({ image, alt }) {
  const { t } = useTranslation("catalog");

  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    async function loadImage() {
      setSrc("");
      setFailed(false);

      if (!image?.file_url) {
        return;
      }

      try {
        const blob = await fetchProductImageBlob(image);
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    }

    loadImage();

    return () => {
      cancelled = true;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [image?.id, image?.file_url, image?.updatedAt]);

  if (!image) {
    return <div className="po-thumb po-thumb--empty">—</div>;
  }

  if (failed) {
    return <div className="po-thumb po-thumb--empty">{t("productOverview.imageLoadFailed")}</div>;
  }

  if (!src) {
    return <div className="po-thumb po-thumb--empty">...</div>;
  }

  return (
    <img
      className="po-thumb"
      src={src}
      alt={alt || image.original_name || t("productOverview.productImageAlt")}
      loading="lazy"
    />
  );
}

/**
 * Debounce hook used for free-text search.
 *
 * Search is client-side on the currently loaded list. This keeps the overview
 * responsive while avoiding excessive state churn while typing.
 */
function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

/* -------------------------------------------------------------------------- */
/* Column resizing                                                             */
/* -------------------------------------------------------------------------- */

const COL_KEYS = [
  "image",
  "category",
  "product",
  "variant",
  "sku",
  "barcode",
  "content",
  "stockUnit",
  "gross",
  "vat",
  "active",
  "publish",
  "actions",
];

const WIDTHS_STORAGE_KEY = "po_col_widths";
const PAGE_SIZE_STORAGE_KEY = "po_page_size";
const CATEGORY_FILTER_STORAGE_KEY = "po_category_ids";
const ACTIVITY_FILTER_STORAGE_KEY = "po_activity";
const PUBLISH_FILTER_STORAGE_KEY = "po_publish_filter";

const MIN_COL_W = 36;
const MAX_AUTOFIT_W = 520;

function loadJsonStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function saveJsonStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors, for example private mode or quota issues.
  }
}

function getCanvasFontFromElement(el) {
  const cs = window.getComputedStyle(el);
  const fontStyle = cs.fontStyle || "normal";
  const fontVariant = cs.fontVariant || "normal";
  const fontWeight = cs.fontWeight || "400";
  const fontSize = cs.fontSize || "12px";
  const fontFamily =
    cs.fontFamily ||
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  return `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`;
}

function estimateColumnWidth(key, headerLabel, rows, tableEl) {
  if (key === "image") return 76;
  if (key === "actions") return 260;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  ctx.font = tableEl
    ? getCanvasFontFromElement(tableEl)
    : 'normal normal 400 12px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  const getText = (k, r) => {
    switch (k) {
      case "category":
        return getProductCategoryName(r);
      case "product":
        return `${r.brand || ""} ${r.name_public || ""}`.trim();
      case "variant":
        return `${r.variant_label || ""} ${r.packaging_label || ""}`.trim();
      case "sku":
        return r.sku || "";
      case "barcode":
        return r.barcode || "";
      case "content":
        return formatContent(r);
      case "stockUnit":
        return getUnitSymbol(r.stockUnit, r.stock_unit_code);
      case "gross":
        return formatMoney(r.price_gross);
      case "vat":
        return `${formatDecimal(r.vat_rate_percent, 2)}%`;
      case "active":
        return asBool(r.is_active) ? "Active" : "Inactive";
      case "publish":
        return asBool(r.publish_to_website) ? "Publish" : "Not published";
      default:
        return "";
    }
  };

  let max = ctx.measureText(headerLabel || key).width;

  for (const row of rows) {
    const w = ctx.measureText(getText(key, row)).width;
    if (w > max) max = w;
  }

  let width = Math.ceil(max + 34);

  const minimums = {
    category: 140,
    product: 220,
    variant: 130,
    sku: 150,
    barcode: 150,
    content: 100,
    stockUnit: 90,
    gross: 110,
    vat: 80,
    active: 90,
    publish: 110,
  };

  if (minimums[key]) width = Math.max(width, minimums[key]);

  return Math.max(MIN_COL_W, Math.min(width, MAX_AUTOFIT_W));
}

/* -------------------------------------------------------------------------- */
/* Main component                                                              */
/* -------------------------------------------------------------------------- */

const ALL_ACTIVITY = ["Active", "Inactive"];

const PUBLISH_OPTIONS = [
  { value: "all" },
  { value: "publish" },
  { value: "hidden" },
];

export default function ProductOverviewPage() {
  const { t } = useTranslation(["common", "catalog"]);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * ProductOverview return target.
   *
   * This value is passed to ProductPage when the operator opens create/edit.
   * Because ProductOverview writes its filter/page state into the URL, this path
   * is enough to reconstruct the exact overview state when the user comes back.
   */
  const returnToOverviewPath = useMemo(
    () => buildReturnToPath(location),
    [location]
  );

  /**
   * UI-only translations for technical filter values.
   *
   * The backend and URL/localStorage state still use stable values such as
   * "Active", "Inactive", "publish" and "hidden". Only the visible labels are
   * translated here, which keeps the existing filtering and returnTo behavior
   * unchanged while making the operator-facing screen bilingual.
   */
  const activityValueLabels = useMemo(
    () => ({
      Active: t("common:status.active"),
      Inactive: t("common:status.inactive"),
    }),
    [t]
  );

  const getActivityDisplayLabel = useCallback(
    (value) => activityValueLabels[value] || value,
    [activityValueLabels]
  );

  const publishOptions = useMemo(
    () => [
      { value: "all", label: t("catalog:productOverview.publishOptions.all") },
      {
        value: "publish",
        label: t("catalog:productOverview.publishOptions.publish"),
      },
      {
        value: "hidden",
        label: t("catalog:productOverview.publishOptions.hidden"),
      },
    ],
    [t]
  );

  const getPublishDisplayLabel = useCallback(
    (value) => publishOptions.find((x) => x.value === value)?.label || value,
    [publishOptions]
  );

  const columnLabels = useMemo(
    () => ({
      image: t("catalog:productOverview.columns.image"),
      category: t("catalog:productOverview.columns.category"),
      product: t("catalog:productOverview.columns.product"),
      variant: t("catalog:productOverview.columns.variant"),
      sku: t("catalog:productOverview.columns.sku"),
      barcode: t("catalog:productOverview.columns.barcode"),
      content: t("catalog:productOverview.columns.content"),
      stockUnit: t("catalog:productOverview.columns.stockUnit"),
      gross: t("catalog:productOverview.columns.gross"),
      vat: t("catalog:productOverview.columns.vat"),
      active: t("catalog:productOverview.columns.active"),
      publish: t("catalog:productOverview.columns.publish"),
      actions: t("catalog:productOverview.columns.actions"),
    }),
    [t]
  );

  /* ------------------------------------------------------------------------ */
  /* Data state                                                                */
  /* ------------------------------------------------------------------------ */

  const [serverProducts, setServerProducts] = useState([]);
  const [barcodeProducts, setBarcodeProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(false);
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [barcodeBannerMsg, setBarcodeBannerMsg] = useState("");

  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* ------------------------------------------------------------------------ */
  /* Filters                                                                   */
  /* ------------------------------------------------------------------------ */

  const [filtersOpen, setFiltersOpen] = useState(true);

  const [searchText, setSearchText] = useState(() => {
    return String(searchParams.get("q") || "");
  });

  const debouncedSearchText = useDebouncedValue(searchText, 350);

  const [barcodeInput, setBarcodeInput] = useState(() => {
    return String(searchParams.get("barcode") || "");
  });

  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => {
    const fromUrl = readCsvParam(searchParams, "categories");

    if (fromUrl.length) {
      return fromUrl;
    }

    const saved = loadJsonStorage(CATEGORY_FILTER_STORAGE_KEY, []);
    return Array.isArray(saved) ? saved : [];
  });

  const [selectedActivity, setSelectedActivity] = useState(() => {
    const fromUrl = readCsvParam(searchParams, "activity").filter((s) =>
      ALL_ACTIVITY.includes(s)
    );

    if (fromUrl.length) {
      return fromUrl;
    }

    const saved = loadJsonStorage(ACTIVITY_FILTER_STORAGE_KEY, ["Active"]);
    const cleaned = Array.isArray(saved)
      ? saved.filter((s) => ALL_ACTIVITY.includes(s))
      : [];

    return cleaned.length ? cleaned : ["Active"];
  });

  const [publishFilter, setPublishFilter] = useState(() => {
    const fromUrl = String(searchParams.get("publish") || "").trim();

    if (PUBLISH_OPTIONS.some((opt) => opt.value === fromUrl)) {
      return fromUrl;
    }

    const saved = localStorage.getItem(PUBLISH_FILTER_STORAGE_KEY);
    return saved || "all";
  });

  const isBarcodeMode = useMemo(
    () => Boolean(String(barcodeInput || "").trim()),
    [barcodeInput]
  );

  useEffect(() => {
    saveJsonStorage(CATEGORY_FILTER_STORAGE_KEY, selectedCategoryIds);
  }, [selectedCategoryIds]);

  useEffect(() => {
    saveJsonStorage(ACTIVITY_FILTER_STORAGE_KEY, selectedActivity);
  }, [selectedActivity]);

  useEffect(() => {
    try {
      localStorage.setItem(PUBLISH_FILTER_STORAGE_KEY, publishFilter);
    } catch {
      // Ignore storage errors.
    }
  }, [publishFilter]);

  useEffect(() => {
    if (!errorMsg && !successMsg && !barcodeBannerMsg) return;

    const id = setTimeout(() => {
      setErrorMsg("");
      setSuccessMsg("");
      setBarcodeBannerMsg("");
    }, 6500);

    return () => clearTimeout(id);
  }, [errorMsg, successMsg, barcodeBannerMsg]);

  /* ------------------------------------------------------------------------ */
  /* Refs                                                                      */
  /* ------------------------------------------------------------------------ */

  const tableRef = useRef(null);
  const tableWidthRef = useRef(null);
  const scrollerRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const searchInputRef = useRef(null);

  /* ------------------------------------------------------------------------ */
  /* Column widths                                                             */
  /* ------------------------------------------------------------------------ */

  const [colWidths, setColWidths] = useState(() =>
    loadJsonStorage(WIDTHS_STORAGE_KEY, {})
  );

  useEffect(() => {
    saveJsonStorage(WIDTHS_STORAGE_KEY, colWidths);
  }, [colWidths]);

  const resizeInfoRef = useRef(null);
  const displayProductsForSizingRef = useRef([]);

  const onResizeMove = useCallback((ev) => {
    const info = resizeInfoRef.current;
    if (!info) return;

    const delta = ev.clientX - info.startX;
    const nextWidth = Math.max(MIN_COL_W, info.startWidth + delta);

    setColWidths((w) => ({
      ...w,
      [info.key]: nextWidth,
    }));
  }, []);

  const endResize = useCallback(() => {
    resizeInfoRef.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", endResize);
  }, [onResizeMove]);

  const startResize = useCallback(
    (e, key) => {
      e.preventDefault();
      e.stopPropagation();

      const th = e.currentTarget.closest("th");
      const rect = th.getBoundingClientRect();

      resizeInfoRef.current = {
        key,
        startX: e.clientX,
        startWidth: rect.width,
      };

      window.addEventListener("mousemove", onResizeMove);
      window.addEventListener("mouseup", endResize);
    },
    [onResizeMove, endResize]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onResizeMove);
      window.removeEventListener("mouseup", endResize);
    };
  }, [onResizeMove, endResize]);

  const widthFor = useCallback(
    (key) => {
      const v = colWidths[key];
      return typeof v === "number" && v > 0 ? `${v}px` : undefined;
    },
    [colWidths]
  );

  const widthAttrFor = useCallback(
    (key) => {
      const v = colWidths[key];
      return typeof v === "number" && v > 0 ? v : undefined;
    },
    [colWidths]
  );

  const cellWidthStyle = useCallback(
    (key) => {
      const px = widthFor(key);
      return px ? { width: px, maxWidth: px } : undefined;
    },
    [widthFor]
  );

  const autoFitColumn = useCallback((key) => {
    const headers = columnLabels;

    const width = estimateColumnWidth(
      key,
      headers[key] || key,
      displayProductsForSizingRef.current,
      tableRef.current
    );

    setColWidths((w) => ({ ...w, [key]: width }));
  }, [columnLabels]);

  const autoFitAll = useCallback(() => {
    const headers = columnLabels;

    const rows = displayProductsForSizingRef.current;
    const next = {};

    for (const key of COL_KEYS) {
      next[key] = estimateColumnWidth(
        key,
        headers[key] || key,
        rows,
        tableRef.current
      );
    }

    setColWidths(next);
  }, [columnLabels]);

  const resetAllWidths = useCallback(() => {
    localStorage.removeItem(WIDTHS_STORAGE_KEY);
    setColWidths({});
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Data loading                                                              */
  /* ------------------------------------------------------------------------ */

  const categoriesAbortRef = useRef(null);
  const productsAbortRef = useRef(null);

  const loadCategories = useCallback(async () => {
    categoriesAbortRef.current?.abort?.();
    const controller = new AbortController();
    categoriesAbortRef.current = controller;

    try {
      const data = await fetchProductCategories({ active: true });
      if (controller.signal.aborted) return;
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isRequestCanceled(e) || controller.signal.aborted) return;
      console.error("[ProductOverview] loadCategories failed:", e);
      setErrorMsg(t("catalog:productOverview.messages.loadCategoriesFailed"));
    }
  }, []);

  const buildServerParams = useCallback(() => {
    const params = {};

    // Activity filter:
    // - one selected => server-side active filter,
    // - both selected => no active filter,
    // - none selected => display list becomes empty.
    if (selectedActivity.length === 1) {
      params.active = selectedActivity[0] === "Active";
    }

    // If one category is selected, backend can filter directly.
    // If multiple are selected, fetch broader list and filter client-side.
    if (selectedCategoryIds.length === 1) {
      params.category_id = selectedCategoryIds[0];
    }

    // If backend supports publish_to_website, this helps.
    // If not, client-side filtering still applies below.
    if (publishFilter === "publish") params.publish_to_website = true;
    if (publishFilter === "hidden") params.publish_to_website = false;

    return params;
  }, [selectedActivity, selectedCategoryIds, publishFilter]);

  const loadProducts = useCallback(async () => {
    productsAbortRef.current?.abort?.();
    const controller = new AbortController();
    productsAbortRef.current = controller;

    if (!selectedActivity.length) {
      setServerProducts([]);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const params = buildServerParams();
      const data = await fetchProducts(params);

      if (controller.signal.aborted) return;
      setServerProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isRequestCanceled(e) || controller.signal.aborted) return;
      console.error("[ProductOverview] loadProducts failed:", e);
      setErrorMsg(t("catalog:productOverview.messages.loadProductsFailed"));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [buildServerParams, selectedActivity.length]);

  useEffect(() => {
    loadCategories();

    return () => {
      categoriesAbortRef.current?.abort?.();
      productsAbortRef.current?.abort?.();
    };
  }, [loadCategories]);

  useEffect(() => {
    if (isBarcodeMode) return;
    loadProducts();
  }, [isBarcodeMode, loadProducts]);

  /* ------------------------------------------------------------------------ */
  /* Derived display list                                                      */
  /* ------------------------------------------------------------------------ */

  const displayProducts = useMemo(() => {
    if (isBarcodeMode) return barcodeProducts;

    let list = Array.isArray(serverProducts) ? serverProducts : [];

    // Defensive client-side filtering keeps the overview stable even if backend
    // initially supports only a subset of filters.
    if (selectedCategoryIds.length) {
      const selected = new Set(selectedCategoryIds.map(String));
      list = list.filter((row) => selected.has(String(row.category_id)));
    }

    if (selectedActivity.length !== ALL_ACTIVITY.length) {
      const activeSet = new Set(selectedActivity);
      list = list.filter((row) => {
        const label = asBool(row.is_active) ? "Active" : "Inactive";
        return activeSet.has(label);
      });
    }

    if (publishFilter === "publish") {
      list = list.filter((row) => asBool(row.publish_to_website));
    } else if (publishFilter === "hidden") {
      list = list.filter((row) => !asBool(row.publish_to_website));
    }

    const q = String(debouncedSearchText || "").trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const haystack = [
          row.name_public,
          row.brand,
          row.sku,
          row.barcode,
          row.variant_group_key,
          row.variant_label,
          row.packaging_label,
          row.description_short,
          getProductCategoryName(row),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(q);
      });
    }

    return list;
  }, [
    isBarcodeMode,
    barcodeProducts,
    serverProducts,
    selectedCategoryIds,
    selectedActivity,
    publishFilter,
    debouncedSearchText,
  ]);

  useEffect(() => {
    displayProductsForSizingRef.current = displayProducts;
  }, [displayProducts]);

  /* ------------------------------------------------------------------------ */
  /* Shared pagination                                                         */
  /* ------------------------------------------------------------------------ */

  const initialPageSize = useMemo(() => {
    const fromUrl = readPositiveIntParam(searchParams, "pageSize", 0);

    if (fromUrl > 0) {
      return fromUrl;
    }

    const saved = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0 ? saved : 50;
  }, [searchParams]);

  /**
   * Initial page requested by URL.
   *
   * useClientPagination owns page state internally and currently accepts only
   * initialPageSize. Therefore we restore the requested page with setPage after
   * the hook is initialized.
   */
  const initialPageFromUrl = useMemo(() => {
    return readPositiveIntParam(searchParams, "page", 1);
  }, [searchParams]);

  const {
    page,
    pageSize,
    pageCount: totalPages,
    totalRows,
    paginatedItems: paginated,
    setPage,
    setPageSize,
    goPrev,
    goNext,
  } = useClientPagination(displayProducts, {
    initialPageSize,
  });

  /**
   * URL synchronization guard.
   *
   * Why this guard exists:
   * - filter state is initialized from URL immediately,
   * - pagination page must be restored after useClientPagination is created,
   * - we must avoid overwriting the URL with page=1 before the requested page is
   *   restored.
   */
  const productOverviewUrlSyncReadyRef = useRef(false);

  /**
   * Restore requested page from URL once.
   *
   * If URL contains page=3, the overview should open page 3 after returning from
   * ProductPage. If the value is invalid or missing, page 1 is used.
   */
  useEffect(() => {
    if (productOverviewUrlSyncReadyRef.current) return;

    if (initialPageFromUrl > 1 && page !== initialPageFromUrl) {
      setPage(initialPageFromUrl);
      return;
    }

    productOverviewUrlSyncReadyRef.current = true;
  }, [initialPageFromUrl, page, setPage]);

  /**
   * Write current overview state to URL.
   *
   * This is the key part of the "returnTo" pattern:
   * - ProductOverview state is represented in the address bar,
   * - ProductPage only receives a returnTo path,
   * - coming back restores filters and pagination without global state hacks.
   *
   * We intentionally keep localStorage as a secondary convenience layer for some
   * defaults, but URL is now the authoritative state for navigation return.
   */
  useEffect(() => {
    if (!productOverviewUrlSyncReadyRef.current) return;

    const next = new URLSearchParams();

    const q = String(searchText || "").trim();
    if (q) next.set("q", q);

    const barcode = String(barcodeInput || "").trim();
    if (barcode) next.set("barcode", barcode);

    if (selectedCategoryIds.length) {
      next.set("categories", selectedCategoryIds.map(String).join(","));
    }

    const isDefaultActivity =
      selectedActivity.length === 1 && selectedActivity[0] === "Active";

    if (!isDefaultActivity) {
      next.set("activity", selectedActivity.join(","));
    }

    if (publishFilter !== "all") {
      next.set("publish", publishFilter);
    }

    if (page > 1) {
      next.set("page", String(page));
    }

    if (pageSize !== 50) {
      next.set("pageSize", String(pageSize));
    }

    setSearchParams(next, { replace: true });
  }, [
    searchText,
    barcodeInput,
    selectedCategoryIds,
    selectedActivity,
    publishFilter,
    page,
    pageSize,
    setSearchParams,
  ]);

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      setPageSize(nextPageSize);

      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(nextPageSize));
      } catch {
        // Ignore storage errors.
      }
    },
    [setPageSize]
  );

  const summary = useMemo(() => {
    const acc = {
      total: displayProducts.length,
      active: 0,
      inactive: 0,
      published: 0,
      withBarcode: 0,
      withImage: 0,
    };

    for (const row of displayProducts) {
      if (asBool(row.is_active)) acc.active += 1;
      else acc.inactive += 1;

      if (asBool(row.publish_to_website)) acc.published += 1;
      if (isNonEmpty(row.barcode)) acc.withBarcode += 1;
      if (imageList(row).length > 0) acc.withImage += 1;
    }

    return acc;
  }, [displayProducts]);

  /* ------------------------------------------------------------------------ */
  /* Barcode lookup                                                            */
  /* ------------------------------------------------------------------------ */

  const runBarcodeLookup = useCallback(
    async (rawBarcode = barcodeInput) => {
      const barcode = String(rawBarcode || "").trim();

      setPage(1);
      setBarcodeInput(barcode);
      setBarcodeBannerMsg("");
      setErrorMsg("");
      setSuccessMsg("");

      if (!barcode) {
        setBarcodeProducts([]);
        return;
      }

      setBarcodeLoading(true);

      try {
        const product = await fetchProductByBarcode(barcode);
        setBarcodeProducts(product ? [product] : []);

        if (!product) {
          setBarcodeBannerMsg(t("catalog:productOverview.messages.noBarcodeProduct"));
        }
      } catch (e) {
        const status = e?.status || e?.response?.status;

        if (status === 404) {
          setBarcodeProducts([]);
          setBarcodeBannerMsg(t("catalog:productOverview.messages.noBarcodeProduct"));
        } else {
          console.error("[ProductOverview] barcode lookup failed:", e);
          setBarcodeProducts([]);
          setErrorMsg(t("catalog:productOverview.messages.barcodeLookupFailed"));
        }
      } finally {
        setBarcodeLoading(false);
      }
    },
    [barcodeInput, setPage]
  );

  const clearBarcodeMode = useCallback(() => {
    setPage(1);
    setBarcodeInput("");
    setBarcodeProducts([]);
    setBarcodeBannerMsg("");
    setErrorMsg("");
    setSuccessMsg("");

    requestAnimationFrame(() => {
      barcodeInputRef.current?.focus?.();
      try {
        barcodeInputRef.current?.select?.();
      } catch {
        // Ignore selection errors.
      }
    });
  }, [setPage]);

  /**
   * Restore barcode mode from URL.
   *
   * Normal operator flow:
   * - barcode mode is activated by pressing Enter in the barcode field.
   *
   * Return flow:
   * - if the user left ProductOverview while barcode mode was active,
   * - returnTo contains ?barcode=...,
   * - this effect performs the exact lookup once after remount.
   */
  const restoredBarcodeLookupRef = useRef(false);

  useEffect(() => {
    if (restoredBarcodeLookupRef.current) return;

    const barcode = String(searchParams.get("barcode") || "").trim();
    if (!barcode) {
      restoredBarcodeLookupRef.current = true;
      return;
    }

    restoredBarcodeLookupRef.current = true;
    runBarcodeLookup(barcode);
  }, [runBarcodeLookup, searchParams]);  

  const focusBarcodeForScan = useCallback(() => {
    requestAnimationFrame(() => {
      barcodeInputRef.current?.focus?.();
      try {
        barcodeInputRef.current?.select?.();
      } catch {
        // Ignore selection errors.
      }
    });
  }, []);

  const handleBarcodeKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      runBarcodeLookup(e.currentTarget.value);
    },
    [runBarcodeLookup]
  );

  /**
   * Lightweight scanner catcher.
   *
   * Many barcode scanners act like keyboards:
   * - they type characters very fast,
   * - then send Enter.
   *
   * This catcher supports scanning even when the barcode field is not focused,
   * but it is intentionally conservative:
   * - it ignores normal slow typing,
   * - it requires a minimum length,
   * - it commits only on Enter.
   */
  useEffect(() => {
    const MIN_SCAN_LEN = 6;
    const MAX_GAP_MS = 45;
    const HARD_RESET_MS = 250;

    let buf = "";
    let lastAt = 0;
    let maxGap = 0;

    const reset = () => {
      buf = "";
      lastAt = 0;
      maxGap = 0;
    };

    const looksLikeScan = () => {
      if (buf.length < MIN_SCAN_LEN) return false;
      return maxGap <= MAX_GAP_MS;
    };

    const onKeyDownCapture = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const tag = document.activeElement?.tagName || "";
      const activeIsEditable = /^(INPUT|SELECT|TEXTAREA)$/.test(tag);

      // If the user is typing in another field, do not hijack it.
      // Barcode input itself has its own Enter lookup.
      if (
        activeIsEditable &&
        document.activeElement !== barcodeInputRef.current
      ) {
        return;
      }

      const now = Date.now();

      if (e.key === "Enter") {
        if (looksLikeScan()) {
          e.preventDefault();
          e.stopPropagation();
          runBarcodeLookup(buf);
        }
        reset();
        return;
      }

      if (!e.key || e.key.length !== 1) return;

      if (lastAt && now - lastAt > HARD_RESET_MS) reset();

      if (lastAt) maxGap = Math.max(maxGap, now - lastAt);
      lastAt = now;
      buf += e.key;
    };

    window.addEventListener("keydown", onKeyDownCapture, true);

    return () => {
      window.removeEventListener("keydown", onKeyDownCapture, true);
      reset();
    };
  }, [runBarcodeLookup]);

  /* ------------------------------------------------------------------------ */
  /* Filter actions                                                            */
  /* ------------------------------------------------------------------------ */

  const toggleCategory = useCallback(
    (categoryId) => {
      const id = String(categoryId);
      setPage(1);
      setSelectedCategoryIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    },
    [setPage]
  );

  const clearCategories = useCallback(() => {
    setPage(1);
    setSelectedCategoryIds([]);
  }, [setPage]);

  const toggleActivity = useCallback(
    (label) => {
      setPage(1);
      setSelectedActivity((prev) =>
        prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
      );
    },
    [setPage]
  );

  const selectAllActivity = useCallback(() => {
    setPage(1);
    setSelectedActivity([...ALL_ACTIVITY]);
  }, [setPage]);

  const resetFilters = useCallback(() => {
    setPage(1);
    setSearchText("");
    setBarcodeInput("");
    setBarcodeProducts([]);
    setSelectedCategoryIds([]);
    setSelectedActivity(["Active"]);
    setPublishFilter("all");
    setBarcodeBannerMsg("");
    setErrorMsg("");
    setSuccessMsg("");
  }, [setPage]);

  const toggleFiltersOpen = useCallback(() => {
    setFiltersOpen((v) => !v);
  }, []);

  const activeFilterChips = useMemo(() => {
    const chips = [];

    if (isBarcodeMode) {
      chips.push({
        key: "barcode",
        label: t("catalog:productOverview.chips.barcode", { value: String(barcodeInput).trim() }),
        kind: "barcode",
        onClear: clearBarcodeMode,
      });
      return chips;
    }

    if (selectedCategoryIds.length) {
      const selectedNames = selectedCategoryIds
        .map((id) => categories.find((cat) => String(cat.id) === String(id)))
        .filter(Boolean)
        .map((cat) => cat.name_public);

      const pretty =
        selectedNames.length <= 2
          ? selectedNames.join(", ")
          : `${selectedNames.slice(0, 2).join(", ")} +${
              selectedNames.length - 2
            }`;

      chips.push({
        key: "categories",
        label: t("catalog:productOverview.chips.categories", { value: pretty || selectedCategoryIds.length }),
        onClear: clearCategories,
      });
    }

    if (searchText.trim()) {
      chips.push({
        key: "search",
        label: t("catalog:productOverview.chips.search", { value: searchText.trim() }),
        onClear: () => {
          setPage(1);
          setSearchText("");
        },
      });
    }

    const defaultActiveOnly =
      selectedActivity.length === 1 && selectedActivity[0] === "Active";

    if (!defaultActiveOnly) {
      chips.push({
        key: "activity",
        label:
          selectedActivity.length === 0
            ? t("catalog:productOverview.chips.activityNone")
            : selectedActivity.length === ALL_ACTIVITY.length
            ? t("catalog:productOverview.chips.activityAll")
            : t("catalog:productOverview.chips.activity", {
                value: selectedActivity.map(getActivityDisplayLabel).join(", "),
              }),
        onClear: () => {
          setPage(1);
          setSelectedActivity(["Active"]);
        },
      });
    }

    if (publishFilter !== "all") {
      const label = getPublishDisplayLabel(publishFilter);

      chips.push({
        key: "publish",
        label,
        onClear: () => {
          setPage(1);
          setPublishFilter("all");
        },
      });
    }

    return chips;
  }, [
    isBarcodeMode,
    barcodeInput,
    clearBarcodeMode,
    selectedCategoryIds,
    categories,
    clearCategories,
    searchText,
    selectedActivity,
    publishFilter,
    t,
    getActivityDisplayLabel,
    getPublishDisplayLabel,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Keyboard shortcut                                                         */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key === "/" &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey &&
        !/^(INPUT|SELECT|TEXTAREA)$/.test(document.activeElement?.tagName || "")
      ) {
        e.preventDefault();
        searchInputRef.current?.focus?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Row actions                                                               */
  /* ------------------------------------------------------------------------ */

  const handleCreate = useCallback(() => {
    navigate("/catalog/products/new", {
      state: {
        /**
         * Preserve the exact overview state.
         *
         * ProductPage does not need to understand filters, categories, activity
         * flags, page size or pagination. It only receives the full return URL.
         */
        returnTo: returnToOverviewPath,
      },
    });
  }, [navigate, returnToOverviewPath]);

  const handleEdit = useCallback(
    (row) => {
      navigate(`/catalog/products/${row.id}`, {
        state: {
          product: row,
          phase: "edit",

          /**
           * Return target with current ProductOverview filters and pagination.
           *
           * This prevents the annoying UX where the operator filters the overview,
           * opens one product, returns, and loses the selected overview context.
           */
          returnTo: returnToOverviewPath,
        },
      });
    },
    [navigate, returnToOverviewPath]
  );

  const handleToggleActive = useCallback(
    async (row) => {
      if (!row?.id || updatingId) return;

      const nextActive = !asBool(row.is_active);
      setUpdatingId(row.id);
      setErrorMsg("");
      setSuccessMsg("");

      try {
        await toggleProductActive(row.id, nextActive);
        setSuccessMsg(
          nextActive
            ? t("catalog:productOverview.messages.activated")
            : t("catalog:productOverview.messages.deactivated")
        );

        if (isBarcodeMode) {
          await runBarcodeLookup(barcodeInput);
        } else {
          await loadProducts();
        }
      } catch (e) {
        console.error("[ProductOverview] toggle active failed:", e);
        setErrorMsg(e?.message || t("catalog:productOverview.messages.updateActiveFailed"));
      } finally {
        setUpdatingId(null);
      }
    },
    [updatingId, isBarcodeMode, runBarcodeLookup, barcodeInput, loadProducts, t]
  );

  const handleDelete = useCallback(
    async (row) => {
      if (!row?.id || deletingId) return;

      const label = row.sku || row.name_public || `ID ${row.id}`;

      const ok = window.confirm(
        t("catalog:productOverview.confirmDelete", { label })
      );

      if (!ok) return;

      setDeletingId(row.id);
      setErrorMsg("");
      setSuccessMsg("");

      try {
        await deleteProduct(row.id);
        setSuccessMsg(t("catalog:productOverview.messages.deleted"));

        if (isBarcodeMode) {
          clearBarcodeMode();
        } else {
          await loadProducts();
        }
      } catch (e) {
        console.error("[ProductOverview] delete failed:", e);
        setErrorMsg(e?.message || t("catalog:productOverview.messages.deleteFailed"));
      } finally {
        setDeletingId(null);
      }
    }, [deletingId, isBarcodeMode, clearBarcodeMode, loadProducts, t]
  );

  /* ------------------------------------------------------------------------ */
  /* Render helpers                                                            */
  /* ------------------------------------------------------------------------ */

  const activityChip = useMemo(() => {
    if (!selectedActivity.length) return t("catalog:productOverview.chips.activityNone");
    if (selectedActivity.length === ALL_ACTIVITY.length) {
      return t("catalog:productOverview.chips.activityAll");
    }
    return t("catalog:productOverview.chips.activity", {
      value: selectedActivity.map(getActivityDisplayLabel).join(", "),
    });
  }, [selectedActivity, t, getActivityDisplayLabel]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className="product-overview"
      role="main"
      aria-label={t("catalog:productOverview.aria.main")}
    >
      {/* Toolbar */}
      <div className="po-toolbar">
        <h1>{t("catalog:productOverview.title")}</h1>

        <div
          className="po-toolbar-actions"
          role="group"
          aria-label={t("catalog:productOverview.aria.actions")}
        >
          {isBarcodeMode && (
            <div
              className="po-barcode-chip"
              role="status"
              aria-label={t("catalog:productOverview.aria.barcodeModeActive")}
            >
              <span className="po-barcode-chip-label">
                {t("catalog:productOverview.labels.barcode")}:
                <strong className="po-barcode-chip-value">
                  {String(barcodeInput || "").trim()}
                </strong>
              </span>

              <button
                type="button"
                className="po-btn po-btn-secondary po-btn-xs"
                onClick={clearBarcodeMode}
                title={t("catalog:productOverview.titles.exitBarcodeMode")}
              >
                {t("catalog:productOverview.actions.exitClear")}
              </button>

              {barcodeLoading && (
                <span className="po-barcode-chip-loading">{t("common:actions.search")}…</span>
              )}
            </div>
          )}

          <div
            className={`po-toolbar-status ${isBarcodeMode ? "is-disabled" : ""}`}
            role="group"
            aria-label={t("catalog:productOverview.aria.activityFilter")}
            title={
              isBarcodeMode
                ? t("catalog:productOverview.titles.activityIgnoredInBarcode")
                : t("catalog:productOverview.titles.activityFilter")
            }
          >
            <span className="po-toolbar-status-label">{t("catalog:productOverview.labels.activity")}:</span>

            <div className="po-toolbar-status-items">
              {ALL_ACTIVITY.map((label) => {
                const checked = selectedActivity.includes(label);
                const isActive = label === "Active";

                return (
                  <label
                    key={label}
                    className={[
                      "po-status-item",
                      checked && isActive
                        ? "po-status-pill po-status-pill--active is-checked"
                        : "",
                      checked && !isActive
                        ? "po-status-pill po-status-pill--inactive is-checked"
                        : "",
                      isBarcodeMode ? "is-disabled" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isBarcodeMode}
                      onChange={() => toggleActivity(label)}
                    />
                    <span>{getActivityDisplayLabel(label)}</span>
                  </label>
                );
              })}
            </div>

            <button
              type="button"
              className="po-btn po-btn-ghost po-btn-xs"
              onClick={selectAllActivity}
              disabled={isBarcodeMode}
              title={t("catalog:productOverview.titles.showAllActivity")}
            >
              {t("catalog:productOverview.actions.all")}
            </button>
          </div>

          <span
            className={`po-btn po-btn-ghost po-status-summary ${
              isBarcodeMode ? "is-muted" : ""
            }`}
            style={{ cursor: "default" }}
            title={t("catalog:productOverview.titles.currentActivityFilter")}
          >
            {activityChip}
          </span>

          <button
            type="button"
            className="po-btn po-btn-primary po-add-product-toolbar"
            onClick={handleCreate}
            title={t("catalog:productOverview.titles.createProduct")}
          >
            <span className="po-add-product-toolbar__icon" aria-hidden="true">
              +
            </span>
            <span>{t("catalog:productOverview.actions.addProduct")}</span>
          </button>

          <button
            type="button"
            className="po-btn po-btn-secondary"
            onClick={toggleFiltersOpen}
            aria-expanded={filtersOpen}
            aria-controls="product-filters-drawer"
          >
            {filtersOpen
              ? t("catalog:productOverview.actions.hideFilters")
              : t("catalog:productOverview.actions.showFilters")}
          </button>

          <button
            type="button"
            className="po-btn po-btn-ghost"
            onClick={resetFilters}
          >
            {t("common:actions.reset")}
          </button>

          <button
            type="button"
            className="po-btn po-btn-secondary"
            onClick={autoFitAll}
            title={t("catalog:productOverview.titles.autoFitAll")}
          >
            {t("catalog:productOverview.actions.autoFitColumns")}
          </button>

          <button
            type="button"
            className="po-btn po-btn-ghost"
            onClick={resetAllWidths}
            title={t("catalog:productOverview.titles.resetWidths")}
          >
            {t("catalog:productOverview.actions.resetWidths")}
          </button>
        </div>
      </div>

      {/* Messages */}
      {(errorMsg || successMsg || barcodeBannerMsg) && (
        <div
          role="alert"
          className={[
            "po-alert",
            errorMsg || barcodeBannerMsg
              ? "po-alert--error"
              : "po-alert--success",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {errorMsg || barcodeBannerMsg || successMsg}
        </div>
      )}

      {/* Filters drawer */}
      {filtersOpen && (
        <div
          className="po-filters-drawer"
          id="product-filters-drawer"
          aria-label={t("catalog:productOverview.aria.filters")}
        >
          <div className="po-filters-grid">
            {/* Barcode scan block */}
            <div
              className={`po-filter-group po-filter-barcode ${
                isBarcodeMode ? "is-active" : ""
              }`}
            >
              <label htmlFor="productBarcodeScan">{t("catalog:productOverview.fields.scanBarcode")}</label>

              <input
                ref={barcodeInputRef}
                id="productBarcodeScan"
                type="text"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={handleBarcodeKeyDown}
                onFocus={() => {
                  try {
                    barcodeInputRef.current?.select?.();
                  } catch {
                    // Ignore selection errors.
                  }
                }}
                placeholder={t("catalog:productOverview.placeholders.barcode")}
                autoComplete="off"
                inputMode="search"
              />

              <div className="po-inline-actions">
                <button
                  type="button"
                  className="po-btn po-btn-secondary po-btn-xs"
                  onClick={() => runBarcodeLookup(barcodeInput)}
                >
                  {t("common:actions.search")}
                </button>

                <button
                  type="button"
                  className="po-btn po-btn-secondary po-btn-xs"
                  onClick={focusBarcodeForScan}
                >
                  {t("catalog:productOverview.actions.readyToScan")}
                </button>

                <button
                  type="button"
                  className="po-btn po-btn-ghost po-btn-xs"
                  onClick={clearBarcodeMode}
                  disabled={!isBarcodeMode && !barcodeInput}
                >
                  {t("common:actions.clear")}
                </button>
              </div>

              <div className="po-barcode-foot">
                <p className="po-micro-hint">{t("catalog:productOverview.hints.scannerFriendly")}</p>
                <p className="po-hint">
                  {t("catalog:productOverview.hints.barcodeIgnoresFilters")}
                </p>
              </div>
            </div>

            {/* Search block */}
            <div
              className={`po-filter-group po-filter-search ${
                isBarcodeMode ? "is-frozen" : ""
              }`}
            >
              <label htmlFor="productSearch">{t("common:actions.search")}</label>
              <input
                ref={searchInputRef}
                id="productSearch"
                type="text"
                value={searchText}
                onChange={(e) => {
                  setPage(1);
                  setSearchText(e.target.value);
                }}
                placeholder={t("catalog:productOverview.placeholders.search")}
                autoComplete="off"
                inputMode="search"
                disabled={isBarcodeMode}
              />
              <p className="po-field-help">{t("catalog:productOverview.hints.quickSearchShortcut")}</p>
            </div>

            {/* Website publish filter */}
            <div
              className={`po-filter-group po-filter-publish ${
                isBarcodeMode ? "is-frozen" : ""
              }`}
            >
              <label htmlFor="publishFilter">{t("catalog:productOverview.labels.website")}</label>
              <select
                id="publishFilter"
                value={publishFilter}
                onChange={(e) => {
                  setPage(1);
                  setPublishFilter(e.target.value);
                }}
                disabled={isBarcodeMode}
              >
                {publishOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Horizontal category strip */}
            <div
              className={`po-filter-group po-filter-categories ${
                isBarcodeMode ? "is-frozen" : ""
              }`}
            >
              <div className="po-category-header">
                <label>{t("catalog:productOverview.labels.categories")}</label>

                <button
                  type="button"
                  className="po-btn po-btn-ghost po-btn-xs"
                  onClick={clearCategories}
                  disabled={isBarcodeMode || !selectedCategoryIds.length}
                >
                  {t("common:actions.clear")}
                </button>
              </div>

              <div
                className="po-category-strip"
                role="group"
                aria-label={t("catalog:productOverview.aria.categories")}
              >
                {categories.length === 0 ? (
                  <span className="po-category-empty">{t("catalog:productOverview.empty.noCategories")}</span>
                ) : (
                  categories.map((cat) => {
                    const id = String(cat.id);
                    const checked = selectedCategoryIds.includes(id);

                    return (
                      <label
                        key={cat.id}
                        className={[
                          "po-category-pill",
                          checked ? "is-checked" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        title={cat.name_public}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isBarcodeMode}
                          onChange={() => toggleCategory(id)}
                        />
                        <span>{cat.name_public}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterChips.length > 0 && (
        <div
          className="po-active-filters"
          role="region"
          aria-label={t("catalog:productOverview.aria.activeFilters")}
        >
          <div className="po-active-filters-row">
            <span className="po-active-filters-title">{t("catalog:productOverview.labels.activeFilters")}:</span>

            <div className="po-chips" role="list" aria-label={t("catalog:productOverview.aria.filterChips")}>
              {activeFilterChips.map((chip) => (
                <span
                  key={chip.key}
                  className={`po-chip ${
                    chip.kind === "barcode" ? "po-chip--barcode" : ""
                  }`}
                  role="listitem"
                  title={chip.label}
                >
                  <span className="po-chip-text">{chip.label}</span>
                  <button
                    type="button"
                    className={`po-chip-x ${
                      chip.kind === "barcode" ? "is-danger" : ""
                    }`}
                    onClick={() => {
                      setPage(1);
                      chip.onClear?.();
                    }}
                    aria-label={t("catalog:productOverview.aria.clearFilter", { label: chip.label })}
                    title={t("common:actions.clear")}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            <button
              type="button"
              className="po-btn po-btn-ghost po-btn-xs po-clear-all"
              onClick={resetFilters}
            >
              {t("catalog:productOverview.actions.clearAll")}
            </button>
          </div>
        </div>
      )}

      {/* Table + shared pagination */}
      <div className="po-table-wrap">
        <div
          className={`po-table-scroller ${
            loading || barcodeLoading ? "is-loading" : ""
          }`}
          ref={scrollerRef}
          aria-busy={loading || barcodeLoading}
        >
          {(loading || barcodeLoading) && (
            <div className="po-skeleton" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="po-skeleton-row" key={i} />
              ))}
            </div>
          )}

          <div className="po-table-x" ref={tableWidthRef}>
            <table className="product-table" ref={tableRef}>
              <colgroup>
                {COL_KEYS.map((key) => (
                  <col key={key} width={widthAttrFor(key)} />
                ))}
              </colgroup>

              <thead>
                <tr>
                  {COL_KEYS.map((key) => {
                    const label = columnLabels[key] || key;

                    return (
                    <th
                      key={key}
                      className={key === "actions" ? "col-actions" : undefined}
                      style={{ width: widthFor(key) }}
                    >
                      <div className="po-th-inner">
                        <span className="po-th-label" title={label}>
                          {label}
                        </span>
                        <span
                          className="po-col-resizer"
                          title={t("catalog:productOverview.titles.resizeColumn")}
                          onMouseDown={(e) => startResize(e, key)}
                          onDoubleClick={() => autoFitColumn(key)}
                        />
                      </div>
                    </th>
                  );
                  })}
                </tr>
              </thead>

              <tbody>
                {!loading && !barcodeLoading && paginated.length === 0 && (
                  <tr>
                    <td colSpan={COL_KEYS.length} className="po-empty">
                      {isBarcodeMode
                        ? t("catalog:productOverview.empty.noBarcodeProduct")
                        : selectedActivity.length === 0
                        ? t("catalog:productOverview.empty.noActivitySelected")
                        : t("catalog:productOverview.empty.noProducts")}
                    </td>
                  </tr>
                )}

                {paginated.map((row) => {
                  const primaryImage = getPrimaryImage(row);
                  const active = asBool(row.is_active);
                  const published = asBool(row.publish_to_website);

                  return (
                    <tr
                      key={row.id}
                      className={!active ? "po-row-muted" : undefined}
                    >
                      <td>
                        <div className="po-cell" style={cellWidthStyle("image")}>
                          <ProductImageThumb
                            image={primaryImage}
                            alt={row.name_public || t("catalog:productOverview.productImageAlt")}
                          />
                        </div>
                      </td>

                      <td>
                        <div
                          className="po-cell"
                          style={cellWidthStyle("category")}
                          title={getProductCategoryName(row)}
                        >
                          {getProductCategoryName(row)}
                        </div>
                      </td>

                      <td>
                        <div
                          className="po-cell po-cell-product"
                          style={cellWidthStyle("product")}
                          title={`${row.brand || ""} ${row.name_public || ""}`}
                        >
                          {row.brand ? (
                            <span className="po-brand">{row.brand}</span>
                          ) : null}
                          <span>{row.name_public || "—"}</span>
                        </div>
                      </td>

                      <td>
                        <div
                          className="po-cell"
                          style={cellWidthStyle("variant")}
                          title={[row.variant_label, row.packaging_label]
                            .filter(Boolean)
                            .join(" / ")}
                        >
                          {row.variant_label || "—"}
                          {row.packaging_label ? (
                            <span className="po-muted-inline">
                              {" "}
                              / {row.packaging_label}
                            </span>
                          ) : null}
                        </div>
                      </td>

                      <td>
                        <div
                          className="po-cell po-mono"
                          style={cellWidthStyle("sku")}
                          title={row.sku || ""}
                        >
                          {row.sku || "—"}
                        </div>
                      </td>

                      <td>
                        <div
                          className="po-cell po-mono"
                          style={cellWidthStyle("barcode")}
                          title={row.barcode || ""}
                        >
                          {row.barcode || "—"}
                        </div>
                      </td>

                      <td>
                        <div className="po-cell" style={cellWidthStyle("content")}>
                          {formatContent(row)}
                        </div>
                      </td>

                      <td>
                        <div
                          className="po-cell"
                          style={cellWidthStyle("stockUnit")}
                          title={row.stockUnit?.name_public || row.stock_unit_code}
                        >
                          {getUnitSymbol(row.stockUnit, row.stock_unit_code) || "—"}
                        </div>
                      </td>

                      <td>
                        <div className="po-cell" style={cellWidthStyle("gross")}>
                          {formatMoney(row.price_gross)}
                        </div>
                      </td>

                      <td>
                        <div className="po-cell" style={cellWidthStyle("vat")}>
                          {formatDecimal(row.vat_rate_percent, 2)}%
                        </div>
                      </td>

                      <td>
                        <div className="po-cell" style={cellWidthStyle("active")}>
                          <span
                            className={`po-status-chip ${
                              active ? "po-status-active" : "po-status-inactive"
                            }`}
                          >
                            {active ? t("common:status.active") : t("common:status.inactive")}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="po-cell" style={cellWidthStyle("publish")}>
                          <span
                            className={`po-status-chip ${
                              published ? "po-status-publish" : "po-status-hidden"
                            }`}
                          >
                            {published ? t("catalog:productOverview.status.publish") : t("catalog:productOverview.status.hidden")}
                          </span>
                        </div>
                      </td>

                      <td className="po-td-actions">
                        <div
                          className="po-cell po-cell--actions"
                          style={cellWidthStyle("actions")}
                        >
                          <button
                            type="button"
                            className="po-link-btn"
                            onClick={() => handleEdit(row)}
                          >
                            {t("common:actions.edit")}
                          </button>

                          <span className="po-sep" aria-hidden>
                            •
                          </span>

                          <button
                            type="button"
                            className={
                              active
                                ? "po-link-btn po-link-btn-warning"
                                : "po-link-btn po-link-btn-success"
                            }
                            onClick={() => handleToggleActive(row)}
                            disabled={updatingId === row.id}
                          >
                            {updatingId === row.id
                              ? t("catalog:productOverview.actions.updating")
                              : active
                              ? t("catalog:productOverview.actions.deactivate")
                              : t("catalog:productOverview.actions.activate")}
                          </button>

                          <span className="po-sep" aria-hidden>
                            •
                          </span>

                          <button
                            type="button"
                            className="po-link-btn po-link-btn-danger"
                            onClick={() => handleDelete(row)}
                            disabled={deletingId === row.id}
                            title={t("catalog:productOverview.titles.physicalDelete")}
                          >
                            {deletingId === row.id ? t("catalog:productOverview.actions.deleting") : t("common:actions.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <PaginationFooter
          className="po-pagination"
          page={page}
          pageCount={totalPages}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          totalRows={totalRows}
          totalLabel={t("common:pagination.total", { count: totalRows })}
          onPageSizeChange={handlePageSizeChange}
          onPrev={goPrev}
          onNext={goNext}
          summary={
            <div className="po-pagination-summary" aria-label={t("catalog:productOverview.aria.totals")}>
              <span className="po-total-chip">
                <em>{t("catalog:productOverview.totals.total")}</em>
                <strong>{summary.total}</strong>
              </span>
              <span className="po-total-chip">
                <em>{t("catalog:productOverview.totals.active")}</em>
                <strong>{summary.active}</strong>
              </span>
              <span className="po-total-chip">
                <em>{t("catalog:productOverview.totals.published")}</em>
                <strong>{summary.published}</strong>
              </span>
              <span className="po-total-chip">
                <em>{t("catalog:productOverview.totals.barcode")}</em>
                <strong>{summary.withBarcode}</strong>
              </span>
              <span className="po-total-chip">
                <em>{t("catalog:productOverview.totals.images")}</em>
                <strong>{summary.withImage}</strong>
              </span>             
            </div>
          }
        />
      </div>
    </div>
  );
}


